import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import assessment from "../../assets/images/method.png";
import api from "../../api/client";
import { TableNodata } from "../../components/TableNodata";
import { TruncatedText } from "../../components/TruncatedText";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";
import { TimelineSkeleton } from "./DashboardCardSkeletons";

const bulletColors = [
  "#e53935",
  "#8e24aa",
  "#3949ab",
  "#00897b",
  "#f4511e",
  "#43a047",
  "#fdd835",
];

// List height used by the other dashboard cards (Top Performers), and the filter controls' height
const LIST_HEIGHT = 358;
const FILTER_ROW_HEIGHT = 40;

// test_type values accepted by /GetUpcomingScheduledAssessments (Institute Admin / Trainer / Student)
const TEST_TYPES = {
  PROCTORED: "PROCTORED",
  SELF_ASSESSMENT: "SELF_ASSESSMENT",
};
// "ALL" is frontend-only: the API takes one test_type per call, so both types are fetched and merged
const ALL_TEST_TYPES = "ALL";
// Super Admin has no range dropdown; the list is always the next 7 days
const DEFAULT_RANGE = "7_days";

// Super Admin gets organizations -> institutes -> assessments (flattened here);
// the other roles get a flat `data` list.
const parseAssessments = (responseData) =>
  Array.isArray(responseData?.data)
    ? [...responseData.data]
    : (responseData?.organizations || []).flatMap((org) =>
      (org?.institutes || []).flatMap((inst) =>
        (inst?.assessments || []).map((a) => ({
          ...a,
          institute_id: inst?.institute_id,
          institute_name: inst?.institute_name,
        }))
      )
    );

/**
 * UpcomingProctoredAssessments component
 *
 * "Upcoming Assessments" dashboard card (/GetUpcomingScheduledAssessments).
 *   Super Admin:                        { institute_id, range: "7_days" } — Institute dropdown, always next 7 days.
 *   Institute Admin / Trainer / Student: { test_type } — Proctored / Self Assessment; the date range is
 *   decided by the backend (no range sent);
 *   the backend scopes the list from the login token (own institute / his students' tests / tests assigned to him).
 *
 * @param {boolean} showTestTypeFilter - Show the All / Proctored / Self Assessment filter. Defaults to false.
 * @param {boolean} showCounts - Show the question and assigned counts on each item. Defaults to true.
 * @param {boolean} showInstituteFilter - Super Admin mode: Institute dropdown, sends institute_id. Defaults to false.
 * @param {Array} institutes - Dropdown options ({ institute_id, name }) when showInstituteFilter is true.
 * @param {number|null} instituteId - Optional: institute chosen by the page (null = all). When passed, the card's own
 * @param {boolean} clickable - Clicking an item opens the question paper. Pass false for roles that can't open it (Student). Defaults to true.
 *
 * @returns {JSX.Element} The Upcoming Assessments card.
 */
const UpcomingProctoredAssessments = ({
  showTestTypeFilter = false,
  showCounts = true,
  showInstituteFilter = false,
  institutes = [],
  instituteId,
  clickable = true,
}) => {
  const navigate = useNavigate();
  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  // Super Admin: institute picked in the dropdown (null = all institutes)
  const [ownInstituteId, setSelectedInstituteId] = useState(null);
  const instituteControlled = instituteId !== undefined;
  const selectedInstituteId = instituteControlled ? instituteId : ownInstituteId;
  // Institute Admin / Trainer / Student: test type
  const [testType, setTestType] = useState(ALL_TEST_TYPES);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Ignore a response that arrives after the filters have changed again
    let ignore = false;
    const fetchUpcomingAssessments = async () => {
      setLoading(true);
      try {
        // Super Admin: one call by institute. Others: one call per test type ("All" = both types).
        const payloads = showInstituteFilter
          ? [{ institute_id: selectedInstituteId || null, range: DEFAULT_RANGE }]
          : (testType === ALL_TEST_TYPES
            ? [TEST_TYPES.PROCTORED, TEST_TYPES.SELF_ASSESSMENT]
            : [testType]
          ).map((type) => ({ test_type: type })); // range left to the backend default

        // allSettled: if one type's call fails (e.g. "All" = two calls), the other type's results still show
        const results = await Promise.allSettled(
          payloads.map((payload) => api.post("/GetUpcomingScheduledAssessments", payload))
        );
        if (ignore) return;
        results
          .filter((result) => result.status === "rejected")
          .forEach((result) => console.log("Error : ", result.reason));

        // Merge the successful responses, drop duplicates, soonest first
        const seen = new Set();
        const list = results
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value)
          .filter((res) => res?.data?.status === "success")
          .flatMap((res) => parseAssessments(res.data))
          .filter((item) => {
            if (seen.has(item?.assessment_id)) return false;
            seen.add(item?.assessment_id);
            return true;
          });
        list.sort((a, b) =>
          `${a.scheduled_date} ${a.scheduled_time}`.localeCompare(
            `${b.scheduled_date} ${b.scheduled_time}`
          )
        );
        setAssessments(list);
      } catch (error) {
        if (!ignore) setAssessments([]);
        console.log("Error : ", error);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchUpcomingAssessments();
    return () => {
      ignore = true;
    };
  }, [showInstituteFilter, selectedInstituteId, testType]);

  // Small dropdown used for both filters
  const renderFilter = (value, onChange, options, width) => (
    <FormControl size="small" sx={{ width, minWidth: 72, flexShrink: 1 }}>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        MenuProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          transformOrigin: { vertical: "top", horizontal: "right" },
          slotProps: { paper: { sx: { mt: 0.5, minWidth: 150 } } },
        }}
        sx={{
          fontSize: "12px",
          height: 32,
          "& .MuiSelect-select": { py: 0.5, pl: 1, pr: "24px !important" },
          "& .MuiSelect-icon": { right: 2, fontSize: "18px" },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value} sx={{ fontSize: "12px" }}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );


  // Super Admin institute dropdown: "All" first (all institutes), then every institute
  const instituteOptions = [
    { institute_id: null, name: `All ${getLabel("INSTITUTE")}` },
    ...institutes,
  ];

  const listHeight = LIST_HEIGHT;

  // Empty-state text, e.g. "No upcoming self assessments scheduled."
  const typeLabel =
    showInstituteFilter || testType === ALL_TEST_TYPES
      ? "assessments"
      : testType === TEST_TYPES.SELF_ASSESSMENT
        ? "self assessments"
        : "proctored assessments";
  // Show each item's test type when the list can contain more than one type
  const showItemTestType = showInstituteFilter || testType === ALL_TEST_TYPES;

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Grid
        container
        direction="column"
        sx={{
          borderRadius: "8px",
          border: "1px solid #c1c1c6ff",
          background: "#fff",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
          p: 2,
          width: "100%",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {/* Header: title + filters */}
        <Grid
          sx={{
            width: "100%",
            display: "flex",
            gap: 1,
            mb: 1,
            minHeight: FILTER_ROW_HEIGHT,
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "nowrap",
          }}
        >
          <Grid sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0 }}>
            <Box
              component="img"
              src={assessment}
              alt="upcoming"
              sx={{ width: 30, height: 30, flexShrink: 0 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
              Upcoming Assessments
            </Typography>
          </Grid>

          {/* Institute Admin / Trainer / Student: test type next to the title */}
          {showInstituteFilter && !instituteControlled && (
            <Autocomplete
              size="small"
              sx={{
                width: 280,
                minWidth: 0,
                flexShrink: 1,
                "& .MuiOutlinedInput-root": { height: FILTER_ROW_HEIGHT },
              }}
              options={instituteOptions}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.institute_id === value.institute_id}
              value={
                instituteOptions.find((institute) => institute.institute_id === selectedInstituteId) ||
                instituteOptions[0]
              }
              onChange={(event, newValue) => setSelectedInstituteId(newValue?.institute_id ?? null)}
              disableClearable
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <TruncatedText text={option.name} sx={{ width: "100%" }} />
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  sx={{ "& .MuiInputBase-input": { fontSize: "12px" } }}
                />
              )}
            />
          )}
          {showTestTypeFilter && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", ml: "auto", minWidth: 0 }}>
              {renderFilter(
                testType,
                setTestType,
                [
                  { value: ALL_TEST_TYPES, label: "All" },
                  { value: TEST_TYPES.PROCTORED, label: "Proctored" },
                  { value: TEST_TYPES.SELF_ASSESSMENT, label: "Self Assessment" },
                ],
                96
              )}
            </Box>
          )}
        </Grid>


        {/* Upcoming Assessments List */}
        <Box
          component="ul"
          sx={{
            width: "100%",
            minWidth: 0,
            listStyle: "none",
            pl: 0,
            m: 0,
            maxHeight: listHeight,
            minHeight: listHeight,
            overflowY: "auto",
          }}
        >
          {loading ? (
            <TimelineSkeleton />
          ) : assessments.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <TableNodata
                message="No Upcoming Assessments"
                description={
                  showInstituteFilter
                    ? `No ${typeLabel} scheduled in the next 7 days.`
                    : `No upcoming ${typeLabel} scheduled.`
                }
              />
            </Box>
          ) : (
            assessments.map((item, index) => (
              <Box
                component="li"
                key={`upcoming-${item?.assessment_id}-${index}`}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  mb: 1.5,
                  borderBottom: "1px solid #f2f2f2",
                  pb: 1,
                  transition: "all 0.2s ease",
                  cursor: clickable ? "pointer" : "default",
                  "&:hover": {
                    backgroundColor: "#f9fafc",
                    borderRadius: 2,
                    pl: 1,
                  },
                }}
                onClick={() => {
                  if (!clickable) return;
                  navigate("/questionPaperBuilder/view", {
                    state: {
                      assessmentId: item?.assessment_id,
                    },
                  });
                }}
              >
                {/* Stylish Bullet */}
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    mt: 0.8,
                    borderRadius: "50%",
                    background: bulletColors[index % bulletColors.length],
                    flexShrink: 0,
                    boxShadow: "0 0 4px rgba(0,0,0,0.15)",
                  }}
                />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Assessment name + date */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <TruncatedText
                      text={item?.assessment_name}
                      sx={{ fontWeight: 600, fontSize: "14px", color: "text.primary" }}
                    />
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.25,
                        borderRadius: "12px",
                        fontSize: "11px",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        backgroundColor: "#e3f2fd",
                        color: "#1565c0",
                      }}
                    >
                      {/* Date and month, same style as the old timeline ("September 29") */}
                      {dayjs(item?.scheduled_date).format("MMMM D")}
                    </Box>
                  </Box>

                  {/* Institute (only in the Super Admin response) */}
                  {item?.institute_name && (
                    <TruncatedText
                      text={item.institute_name}
                      sx={{ fontSize: "12px", color: "text.secondary" }}
                    />
                  )}

                  {(item?.program_name || item?.batch_name) && (
                    <TruncatedText
                      text={[item?.program_name, item?.batch_name].filter(Boolean).join(" · ")}
                      sx={{ fontSize: "12px", color: "#1e88e5", fontWeight: 500 }}
                    />
                  )}

                  {/* Test type (when the list can mix types) + time + details */}
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
                    {showItemTestType && item?.test_type && `${item.test_type} · `}
                    {/* The date is in the badge above, so only the time is shown here */}
                    {item?.scheduled_time && (
                      <Box component="span" sx={{ fontWeight: "bold", color: "text.primary" }}>
                        {dayjs(`${item.scheduled_date}T${item.scheduled_time}`).format("hh:mm A")}
                      </Box>
                    )}
                    {/* time_limit is "30" (new API) or "30 Minutes" (old) */}
                    {item?.time_limit &&
                      ` · ${/^\d+$/.test(String(item.time_limit).trim()) ? `${item.time_limit} Minutes` : item.time_limit}`}
                    {showCounts && item?.no_of_questions && ` · ${item.no_of_questions} Questions`}
                    {showCounts && ` · ${item?.students_assigned_count ?? 0} ${getLabel("STUDENT")} assigned`}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Grid>
    </Box>
  );
};

export default UpcomingProctoredAssessments;
